import React, { useEffect, useId, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { keyframes } from '@emotion/react'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt'
import ThumbDownOffAltIcon from '@mui/icons-material/ThumbDownOffAlt'
import ThumbUpAltIcon from '@mui/icons-material/ThumbUpAlt'
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpOffAlt'
import {
  Box,
  IconButton,
  Link,
  Pagination,
  Skeleton,
  Tab,
  Tabs,
  Typography,
  styled,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { type CustomPaletteColor, type Theme } from '@mui/material/styles'
import { visuallyHidden } from '@mui/utils'

import { images } from '~assets/images'
import { Avatar, Button, Image } from '~components/common'
import { Banner, Switch } from '~components/internal'
import { IS_PROD, IS_STAGING } from '~config'
import { darkTheme, lightTheme } from '~createTheme'
import { useChatStreamSteps } from '~hooks/useChatStreamSteps'
import { useResponseFeedback } from '~hooks/useResponseFeedback'
import {
  type AllResultsSourceDisplayLink,
  type StreamResponseStep,
  preTextStreamResponseSteps,
  useStreamAIResponse,
} from '~hooks/useStreamAIResponse'
import { type RootState } from '~redux/rootReducer'
import {
  AIInteractionType,
  type EnrichedOrganizationAISettings,
  type SourceDisplayLink,
} from '~types'
import { sanitizeAndProcessLinks, stripHtml } from '~utils/htmlUtils'
import { processResponseStream } from '~utils/responseStreamUtils'

import { initAmplitude } from '../../../AmplitudeInit'

import { ChatStreamSteps } from './ChatStreamSteps'
import { ClearNegativeFeedbackPopover } from './ClearNegativeFeedbackPopover'
import { PrivacyPolicyMessage } from './PrivacyPolicyMessage'
import { ResponseNegativeFeedbackPopover } from './ResponseNegativeFeedbackPopover'
import UpdatedAISearchBar from './UpdatedAISearchBar'

const defaultBanner =
  'https://s3-us-east-2.amazonaws.com/polimorphic-sha256/assets/isometricCity.png'

interface SourcesProps {
  sources: SourceDisplayLink[]
  maxSources?: number
  showTitle?: boolean
  debug?: boolean
  theme: Theme
  isDarkMode?: boolean
  orgAiSettings?: EnrichedOrganizationAISettings
  updatedView?: boolean
}

const focusRingSx = {
  '&:focus-visible': {
    outline: '2px solid rgb(8, 148, 207)',
    outlineOffset: '2px',
  },
}

const StyledLink = styled('a')({
  textDecoration: 'none',
  color: 'inherit',
  '&:hover': {
    textDecoration: 'underline',
  },
  ...focusRingSx,
})

export const Sources: React.FC<SourcesProps> = ({
  sources,
  maxSources,
  showTitle = true,
  debug,
  theme,
  isDarkMode,
  orgAiSettings,
  updatedView = false,
}) => {
  const displaySources = maxSources ? sources.slice(0, maxSources) : sources
  const uniqueSources = Array.from(
    new Map(displaySources.map((source) => [source.page_url, source])).values(),
  )

  return (
    <Box
      sx={{
        m: 3,
        ...(showTitle && { borderTop: '1px solid #888' }),
      }}
    >
      {showTitle && (
        <Typography
          variant='h3'
          sx={{
            fontFamily: orgAiSettings?.font_family || 'inherit',
            margin: '20px 0 30px',
          }}
        >
          Sources
        </Typography>
      )}
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {uniqueSources.map((source, index) => (
          <Button
            key={index}
            href={source?.page_gui_url || source?.page_url || undefined}
            variant='link'
            target='_blank'
            rel='noopener noreferrer'
            sx={{
              display: 'flex',
              alignItems: 'center',
              mt: index === 0 ? 0 : 2.5,
              color: theme.palette.text.primary,
              textDecoration: 'none',
              '&:hover': updatedView
                ? {
                    textDecoration: 'none',
                    '& .source-title': {
                      textDecorationThickness: '2px',
                      textDecoration: 'underline',
                    },
                  }
                : {
                    bgcolor: theme.palette.aiSearch.hover,
                    textDecoration: 'none',
                    '& .source-title': { textDecorationThickness: '2px' },
                  },
              cursor: 'pointer',
            }}
            className='double-blue-border-on-focus'
          >
            <Box sx={{ width: updatedView ? '100%' : 'calc(100% - 45px)' }}>
              {updatedView && (source?.page_gui_url || source?.page_url) && (
                <Typography variant='body2' color='textSecondary'>
                  {source.page_gui_url || source.page_url}
                </Typography>
              )}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography
                  className='source-title'
                  sx={
                    updatedView
                      ? {
                          fontFamily: orgAiSettings?.font_family || 'inherit',
                          fontWeight: 'bold',
                          color: orgAiSettings?.link_color
                            ? `#${orgAiSettings.link_color}`
                            : theme.palette.primary.dark,
                        }
                      : {
                          ...theme.typography.link,
                          color: orgAiSettings?.link_color
                            ? `#${orgAiSettings.link_color}`
                            : theme.palette.primary.dark,
                          textDecoration: 'underline',
                          textDecorationThickness: '1px',
                          fontFamily: orgAiSettings?.font_family || 'inherit',
                          fontWeight: 'bold',
                        }
                  }
                >
                  {source.title}
                </Typography>
              </Box>
              {(source as AllResultsSourceDisplayLink).snippet_pending ? (
                <Box className='source-text' sx={{ mt: 1.25 }} aria-hidden='true'>
                  <Skeleton variant='text' width='95%' height={20} />
                  <Skeleton variant='text' width='70%' height={20} />
                </Box>
              ) : (
                <Typography
                  className='source-text'
                  sx={{
                    fontFamily: orgAiSettings?.font_family || 'inherit',
                    mt: updatedView ? 0 : 1.25,
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden',
                    color: theme.palette.text.primary,
                    textDecoration: 'none',
                  }}
                >
                  {source.text}
                </Typography>
              )}
              {debug && (
                <Typography
                  variant='caption'
                  sx={{
                    fontFamily: orgAiSettings?.font_family || 'inherit',
                    ml: 1,
                    color: theme.palette.text.secondary,
                  }}
                >
                  Score: {source.relevance_score}
                </Typography>
              )}
            </Box>
            {!updatedView && (
              <Box
                sx={{
                  alignItems: 'center',
                  borderRadius: '50%',
                  display: 'flex',
                  height: '25px',
                  justifyContent: 'center',
                  ml: 1.25,
                  mr: 2.5,
                  width: '25px',
                }}
              >
                <OpenInNewIcon
                  sx={{
                    width: '15px',
                    filter: isDarkMode ? 'brightness(0) invert(1)' : 'brightness(0)',
                  }}
                />
              </Box>
            )}
          </Button>
        ))}
      </Box>
    </Box>
  )
}

interface SummarySourcesListProps {
  sources: SourceDisplayLink[]
  maxSources?: number
  theme: Theme
  orgAiSettings?: EnrichedOrganizationAISettings
}

const SummarySourcesList: React.FC<SummarySourcesListProps> = ({
  sources,
  maxSources,
  theme,
  orgAiSettings,
}) => {
  const displaySources = maxSources ? sources.slice(0, maxSources) : sources
  const uniqueSources = Array.from(
    new Map(displaySources.map((source) => [source.page_url, source])).values(),
  )

  return (
    <Box sx={{ mx: 3, mb: 3, borderTop: `1px solid ${theme.palette.aiSearch.border}` }}>
      <Typography
        sx={{
          fontFamily: orgAiSettings?.font_family || 'inherit',
          color: theme.palette.text.secondary,
          mt: 2,
        }}
      >
        Sources
      </Typography>
      <Box component='ul' sx={{ m: 0, mt: 1.5, pl: 3 }}>
        {uniqueSources.map((source, index) => (
          <Box component='li' key={index} sx={{ mb: index === uniqueSources.length - 1 ? 0 : 1 }}>
            <StyledLink
              href={source?.page_gui_url || source?.page_url || undefined}
              target='_blank'
              rel='noopener noreferrer'
              className='double-blue-border-on-focus'
              sx={{
                fontFamily: orgAiSettings?.font_family || 'inherit',
                fontWeight: 600,
                color: orgAiSettings?.link_color
                  ? `#${orgAiSettings.link_color}`
                  : theme.palette.primary.dark,
              }}
            >
              {source.title}
            </StyledLink>
          </Box>
        ))}
      </Box>
    </Box>
  )
}

const SUMMARY_COLLAPSED_HEIGHT_PX = 131

const SKELETON_RESULT_COUNT = 5
const SKELETON_SUMMARY_LINE_COUNT = 3

const SkeletonLine: React.FC<{ height: number }> = ({ height }) => (
  <Skeleton variant='rectangular' width='100%' height={height} sx={{ borderRadius: '4px' }} />
)

const AllResultsSkeleton: React.FC<{ count?: number }> = ({ count = SKELETON_RESULT_COUNT }) => (
  <Box sx={{ m: 3, display: 'flex', flexDirection: 'column', gap: 3 }} aria-hidden='true'>
    {Array.from({ length: count }).map((_, index) => (
      <Box key={index} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <SkeletonLine height={16} />
        <SkeletonLine height={20} />
        <SkeletonLine height={32} />
      </Box>
    ))}
  </Box>
)

const SummarySkeleton: React.FC = () => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} aria-hidden='true'>
    {Array.from({ length: SKELETON_SUMMARY_LINE_COUNT }).map((_, index) => (
      <SkeletonLine key={index} height={24} />
    ))}
  </Box>
)

const formatColor = (color: string | undefined): string => {
  if (!color) return ''
  return color.startsWith('#') ? color : `#${color}`
}

const bounceAnimation = keyframes`
  0%, 20%, 50%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-4px);
  }
  60% {
    transform: translateY(-2px);
  }
`

const initialRenderAnimation = keyframes`
  0% {
    opacity: 0;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1.1);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
`

interface SearchViewProps {
  orgAiSettings: EnrichedOrganizationAISettings
  suggestions?: string[]
  isEmbedded?: boolean
  test?: boolean
  debug?: boolean
  initialQuery?: string
  isInline?: boolean
  showLogo?: boolean
  deviceId?: string
}

interface SearchResultsListProps {
  sources: SourceDisplayLink[]
  pageSize: number
  totalResults: number
  totalPages: number
  page: number
  onPageChange: (page: number) => void
  showCount?: boolean
  showEmptyState?: boolean
  emptyMessage?: string
  listRef?: React.Ref<HTMLDivElement>
  isLoading?: boolean
  pendingCount?: number
  debug?: boolean
  theme: Theme
  isDarkMode?: boolean
  orgAiSettings?: EnrichedOrganizationAISettings
  updatedView?: boolean
}

export const SearchResultsList: React.FC<SearchResultsListProps> = ({
  sources,
  pageSize,
  totalResults,
  totalPages,
  page,
  onPageChange,
  showCount = false,
  showEmptyState = false,
  emptyMessage = 'No results found',
  listRef,
  isLoading = false,
  pendingCount = 0,
  debug,
  theme,
  isDarkMode,
  orgAiSettings,
  updatedView,
}) => (
  <>
    {showCount && totalResults > 0 && (
      <Typography
        sx={{
          mx: 3,
          mb: 2,
          fontFamily: orgAiSettings?.font_family || 'inherit',
          color: theme.palette.text.secondary,
          fontSize: '14px',
        }}
      >
        About {totalResults} results
      </Typography>
    )}
    {showEmptyState && (
      <Typography
        role='status'
        sx={{
          mx: 3,
          mb: 2,
          fontFamily: orgAiSettings?.font_family || 'inherit',
          color: theme.palette.text.secondary,
          fontSize: '14px',
        }}
      >
        {emptyMessage}
      </Typography>
    )}
    <Box
      ref={listRef}
      tabIndex={listRef ? -1 : undefined}
      sx={{
        outline: 'none',
        '&:focus': {
          outline: 'none',
        },
      }}
    >
      {isLoading ? (
        <AllResultsSkeleton />
      ) : (
        <>
          <Sources
            sources={sources}
            maxSources={pageSize}
            showTitle={false}
            debug={debug}
            theme={theme}
            isDarkMode={isDarkMode}
            orgAiSettings={orgAiSettings}
            updatedView={updatedView}
          />
          {pendingCount > 0 && <AllResultsSkeleton count={pendingCount} />}
        </>
      )}
    </Box>
    {totalPages > 1 && (
      <Box
        sx={{
          mt: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Pagination
          aria-label='Search results pagination'
          count={totalPages}
          page={page + 1}
          onChange={(_, value) => onPageChange(value - 1)}
          sx={{
            fontFamily: orgAiSettings?.font_family || 'inherit',
            button: {
              fontFamily: orgAiSettings?.font_family || 'inherit',
            },
          }}
        />
      </Box>
    )}
  </>
)

interface PaginatedSearchResultsProps {
  sources: SourceDisplayLink[]
  pageSize?: number
  debug?: boolean
  theme: Theme
  orgAiSettings?: EnrichedOrganizationAISettings
}

export const PaginatedSearchResults: React.FC<PaginatedSearchResultsProps> = ({
  sources,
  pageSize = 5,
  debug,
  theme,
  orgAiSettings,
}) => {
  const [page, setPage] = useState(0)
  return (
    <SearchResultsList
      sources={sources.slice(page * pageSize, page * pageSize + pageSize)}
      pageSize={pageSize}
      totalResults={sources.length}
      totalPages={Math.ceil(sources.length / pageSize)}
      page={page}
      onPageChange={setPage}
      showCount
      debug={debug}
      theme={theme}
      orgAiSettings={orgAiSettings}
      updatedView
    />
  )
}

interface SearchBoxProps {
  orgAiSettings: EnrichedOrganizationAISettings
  suggestions?: string[]
  test?: boolean
  debug?: boolean
  initialQuery?: string
  disableAnimations?: boolean
  isInline?: boolean
  showLogo?: boolean
  isMobile?: boolean
  deviceId?: string
}

const postMessageScrollHeightOneLine = (): void =>
  window.parent.postMessage({ scrollHeight: document.body.scrollHeight }, '*')

export const SearchBox: React.FC<SearchBoxProps> = ({
  orgAiSettings,
  suggestions,
  test,
  debug,
  initialQuery,
  disableAnimations = false,
  isInline = false,
  showLogo = false,
  isMobile = false,
  deviceId,
}) => {
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const [allResultsFromStreamEnabled, setAllResultsFromStreamEnabled] = useState(false)

  // Derive theme from URL parameters
  const searchParams = new URLSearchParams(window?.location?.search || '')
  const isDarkMode = searchParams.get('theme') === 'dark'
  const theme = isDarkMode ? darkTheme : lightTheme
  const [query, setQuery] = useState<string>('')
  const [submittedQuery, setSubmittedQuery] = useState<string>('')
  const [responseId, setResponseId] = useState<string | null>(null)
  const [response, setResponse] = useState<string>('')
  const [impression, setImpression] = useState<boolean | null>(null)
  const [feedback, setFeedback] = useState<string | null>(null)
  const [responseSources, setResponseSources] = useState<SourceDisplayLink[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [userId, setUserId] = useState<string>('')
  const [aiInteractionId, setAIInteractionId] = useState<string | undefined>(undefined)
  const [activeTab, setActiveTab] = useState(0)
  const [page, setPage] = useState(0)
  const [streamedAllResults, setStreamedAllResults] = useState<AllResultsSourceDisplayLink[]>([])
  const [streamStep, setStreamStep] = useState<StreamResponseStep>()
  const [isStreamingInProgress, setIsStreamingInProgress] = useState(false)
  const [announcementText, setAnnouncementText] = useState<string>('')
  const [searchError, setSearchError] = useState<string | null>(null)
  const [summaryExpanded, setSummaryExpanded] = useState(false)
  const [summaryOverflows, setSummaryOverflows] = useState(false)

  const resultsOnlyEnabled = orgAiSettings.search_layout === 'results_only'
  const combinedViewEnabled = orgAiSettings.search_layout === 'combined'
  const {
    liveSteps: liveStreamSteps,
    stepsExpanded,
    setStepsExpanded,
    onStreamResponse: onChatStreamResponse,
    endTurn: endStreamStepsTurn,
  } = useChatStreamSteps()

  const feedbackLabelId = useId()

  const isPreTextStreamStep = Boolean(
    streamStep && (preTextStreamResponseSteps as readonly string[]).includes(streamStep),
  )

  const {
    onThumbUpClick,
    onThumbDownClick,
    submitPopoverProps,
    clearPopoverProps,
    submitFeedback,
    clearFeedback,
    announcement: feedbackAnnouncement,
  } = useResponseFeedback({
    responseId,
    impression,
    feedback,
    onChange: (update) => {
      if ('impression' in update) {
        setImpression(update.impression ?? null)
      }
      if ('feedback' in update) {
        setFeedback(update.feedback ?? null)
      }
    },
    enabled: test,
  })

  // Flush accumulated streaming text to state
  const flushStreamingText = (): void => {
    if (!streamingTextRef.current) return

    const processedResponse = processResponseStream(streamingTextRef.current)
    setResponse(processedResponse)

    // Update other response data when available
    if (streamingResponseDataRef.current.responseId) {
      setResponseId(streamingResponseDataRef.current.responseId)
    }
    if (streamingResponseDataRef.current.sources) {
      setResponseSources(streamingResponseDataRef.current.sources)
    }

    // Set loading to false once we have content
    if (hasReceivedFirstChunkRef.current === false) {
      setIsLoading(false)
      hasReceivedFirstChunkRef.current = true
    }
  }

  const [debugVisible, setDebugVisible] = useState<boolean>(true)
  const [showInitialAnimation, setShowInitialAnimation] = useState(false)
  const pageSize = 10

  // All-results pagination is client-side over the streamed results
  const allResultsSources = streamedAllResults.slice(page * pageSize, (page + 1) * pageSize)
  const totalResults = streamedAllResults.length
  const totalPages = Math.ceil(totalResults / pageSize)
  const hasStreamedResults = totalResults > 0

  const poliApplySources =
    responseSources.filter((source) => source.source_type === 'SourcePoliProcess') || []
  const poliApplyUniqueSources =
    Array.from(new Map(poliApplySources.map((source) => [source.page_url, source])).values()) || []

  const tabsRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLTextAreaElement>(null)
  const searchBoxRef = useRef<HTMLDivElement>(null)
  const loadingRef = useRef<HTMLDivElement>(null)
  const amplitudeInitialized = useRef(false)
  const allResultsRef = useRef<HTMLDivElement>(null)
  const summaryContentRef = useRef<HTMLDivElement>(null)

  // Throttling refs for streaming text
  const streamingTextRef = useRef<string>('')
  const streamingResponseDataRef = useRef<{
    responseId?: string | null
    sources?: SourceDisplayLink[]
  }>({})
  const throttleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const hasReceivedFirstChunkRef = useRef(false)

  const initAnalyticsIfNeeded = React.useCallback(() => {
    if (amplitudeInitialized.current) return
    if (!(IS_PROD || IS_STAGING)) return
    if (!orgAiSettings) return
    if (orgAiSettings.disable_analytics) return

    amplitudeInitialized.current = true
    initAmplitude().catch((err) => {
      console.error('Failed to initialize Amplitude:', err)
    })
  }, [orgAiSettings])

  // Initialize Amplitude only after any user interaction within the Search UI
  useEffect(() => {
    const container = searchBoxRef.current
    if (!container) return

    const trigger = (): void => {
      initAnalyticsIfNeeded()
      remove()
    }

    const remove = (): void => {
      container.removeEventListener('click', trigger)
      container.removeEventListener('keydown', trigger)
      container.removeEventListener('input', trigger as EventListener)
      container.removeEventListener('focusin', trigger)
    }

    container.addEventListener('click', trigger)
    container.addEventListener('keydown', trigger)
    container.addEventListener('input', trigger as EventListener)
    container.addEventListener('focusin', trigger)

    return (): void => {
      remove()
    }
  }, [initAnalyticsIfNeeded])

  useEffect(() => {
    if (streamStep === 'complete_response' && response && !announcementText) {
      const rafId = requestAnimationFrame(() => {
        const finalText = stripHtml(response).trim()
        if (finalText && finalText !== announcementText) {
          setAnnouncementText(finalText)
        }
      })
      return (): void => cancelAnimationFrame(rafId)
    }
  }, [streamStep, response])

  const { isGeneratingResponseStream, streamAIResponse, abortStream } = useStreamAIResponse(
    (response) => {
      setStreamStep(response.step)
      onChatStreamResponse(response)

      // Record identifiers from every step: Results Only searches complete without any
      // response text, so flushStreamingText would never store them
      if (response.user_id) {
        setUserId(response.user_id)
      }
      if (response.ai_interaction_id) {
        setAIInteractionId(response.ai_interaction_id)
      }

      if (response.all_results) {
        setStreamedAllResults(response.all_results)
      }

      // No response message to render
      if (response.step !== 'partial_response' && response.step !== 'complete_response') {
        return
      }

      // Store the latest streaming text in ref (doesn't cause re-render)
      streamingTextRef.current = response.response_text as string
      streamingResponseDataRef.current = {
        responseId: response.response_id ?? null,
        sources: response.sources,
      }

      // If this is the final response, flush immediately
      if (response.step === 'complete_response') {
        if (throttleTimerRef.current) {
          clearTimeout(throttleTimerRef.current)
          throttleTimerRef.current = null
        }
        flushStreamingText()
        // Reset refs for next stream
        streamingTextRef.current = ''
        streamingResponseDataRef.current = {}
        hasReceivedFirstChunkRef.current = false
        // flushStreamingText is a no-op when the stream produced no text
        setIsLoading(false)
        setIsStreamingInProgress(false)
        return
      }

      // For partial responses, throttle updates to every 130ms
      if (!throttleTimerRef.current) {
        throttleTimerRef.current = setTimeout(() => {
          flushStreamingText()
          throttleTimerRef.current = null
        }, 130)
      }
    },
    (errorMessage) => {
      // Clear any pending throttle timer on error
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
        throttleTimerRef.current = null
      }
      streamingTextRef.current = ''
      streamingResponseDataRef.current = {}
      hasReceivedFirstChunkRef.current = false

      setResponse(errorMessage)
      setSearchError(errorMessage)
      setIsLoading(false)
      setIsStreamingInProgress(false)
      setStreamStep(undefined)
      endStreamStepsTurn()
    },
  )

  // Cleanup throttle timer on unmount
  useEffect(() => {
    return (): void => {
      if (throttleTimerRef.current) {
        clearTimeout(throttleTimerRef.current)
      }
    }
  }, [])

  // Stop any in-flight stream and discard its buffered/throttled text so a late
  // flush from a previous query can't overwrite the new one.
  const resetStreamingState = (): void => {
    abortStream()
    if (throttleTimerRef.current) {
      clearTimeout(throttleTimerRef.current)
      throttleTimerRef.current = null
    }
    streamingTextRef.current = ''
    streamingResponseDataRef.current = {}
    hasReceivedFirstChunkRef.current = false
  }

  const submitQuery = async (text: string): Promise<void> => {
    if (!text.trim()) return

    resetStreamingState()
    endStreamStepsTurn()
    setResponse('')
    setResponseSources([])
    setSearchError(null)
    setStreamedAllResults([])
    setAllResultsFromStreamEnabled(false)
    setIsStreamingInProgress(true)

    const result = await streamAIResponse({
      user_id: userId || undefined,
      ai_interaction_id: aiInteractionId || undefined,
      organization_id: orgAiSettings.organization_id,
      interaction_type: AIInteractionType.enum.SEARCH,
      query: text || '',
      device_id: deviceId,
      is_test: test,
    })

    setAllResultsFromStreamEnabled(result?.include_search_results ?? false)
  }

  const handleSearch = async (searchQuery: string): Promise<void> => {
    if (!searchQuery || searchQuery.length < 2) {
      setResponse(
        'It seems you have not entered a valid question. Please let me know what I can assist you with regarding ' +
          orgAiSettings.organization_name +
          '.',
      )
      setSubmittedQuery('')
      setActiveTab(0)
      return
    }

    setSubmittedQuery(searchQuery)
    setActiveTab(0) // Reset to AI Answer tab on new search
    setPage(0)
    setImpression(null)
    setFeedback(null)
    setAnnouncementText('')
    setStreamedAllResults([])
    setSummaryExpanded(false)
    await submitQuery(searchQuery)
  }

  const handleClear = (): void => {
    resetStreamingState()
    setQuery('')
    setSubmittedQuery('')
    setResponse('')
    setSearchError(null)
    setImpression(null)
    setFeedback(null)
    setPage(0)
    setResponseSources([])
    setStreamedAllResults([])
    setAllResultsFromStreamEnabled(false)
    setActiveTab(0)
    setSummaryExpanded(false)
    setIsLoading(false)
    setIsStreamingInProgress(false)
    setStreamStep(undefined)
    setAnnouncementText('')
    endStreamStepsTurn()
    setTimeout(() => {
      searchInputRef.current?.focus()
    }, 0)
  }

  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!combinedViewEnabled) return
    const element = summaryContentRef.current
    if (!element) return
    setSummaryOverflows(element.scrollHeight > SUMMARY_COLLAPSED_HEIGHT_PX + 1)
  }, [response, combinedViewEnabled, summaryExpanded])

  useEffect(() => {
    if (contentRef.current) {
      const links = contentRef.current.getElementsByTagName('a')
      const eventHandlers: { link: HTMLAnchorElement; handler: (e: Event) => void }[] = []

      for (let i = 0; i < links.length; i++) {
        const link = links[i]
        const clickHandler = (e: Event): void => {
          e.preventDefault()
          const href = link.getAttribute('href')
          if (href) {
            window.open(href, '_blank', 'noopener,noreferrer')
          }
        }

        link.addEventListener('click', clickHandler)
        eventHandlers.push({ link, handler: clickHandler })
      }

      return (): void => {
        eventHandlers.forEach(({ link, handler }) => {
          link.removeEventListener('click', handler)
        })
      }
    }
  }, [response])

  useEffect(() => {
    if (initialQuery) {
      setQuery(initialQuery)
      void handleSearch(initialQuery)
    }
  }, [initialQuery])

  useEffect((): (() => void) | void => {
    if (responseId && !disableAnimations) {
      setShowInitialAnimation(true)
      const timer = setTimeout(() => setShowInitialAnimation(false), 2000)
      return () => clearTimeout(timer)
    }
  }, [responseId, disableAnimations])

  const anyLoading =
    isLoading || isGeneratingResponseStream || isPreTextStreamStep || isStreamingInProgress

  // Move focus to the first result on the All Results tab, but only once streaming has finished
  useEffect(() => {
    if (activeTab === 1 && !anyLoading && allResultsRef.current) {
      requestAnimationFrame(() => {
        if (allResultsRef.current) {
          const firstLink = allResultsRef.current.querySelector('a[href]') as HTMLElement
          if (firstLink) {
            firstLink.focus()
          } else {
            allResultsRef.current.focus()
          }
        }
      })
    }
  }, [activeTab, page, anyLoading])

  useEffect(() => {
    const rafId = requestAnimationFrame(postMessageScrollHeightOneLine)
    return (): void => cancelAnimationFrame(rafId)
  }, [
    response,
    responseSources,
    isLoading,
    anyLoading,
    activeTab,
    page,
    summaryExpanded,
    summaryOverflows,
    streamedAllResults,
    stepsExpanded,
    liveStreamSteps,
  ])

  const isCombinedSummaryLoading = combinedViewEnabled && !response && anyLoading
  const showResultsSkeleton =
    anyLoading &&
    !hasStreamedResults &&
    (combinedViewEnabled || resultsOnlyEnabled || activeTab === 1)
  const showSummarySection =
    !resultsOnlyEnabled &&
    (combinedViewEnabled ? isCombinedSummaryLoading || response !== '' : activeTab === 0)
  const showResultsCount = combinedViewEnabled || resultsOnlyEnabled
  const showAllResultsSection = showResultsCount || activeTab === 1

  const combinedSummaryCardSx = {
    m: 3,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    border: `1px solid ${theme.palette.aiSearch.border}`,
    borderRadius: '8px',
    boxShadow: '0px 0px 7.5px rgba(0, 0, 0, 0.08)',
    backgroundColor: theme.palette.background.paper,
  } as const

  const combinedSummaryHeader = (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <AutoAwesomeRoundedIcon sx={{ fontSize: '24px' }} />
      <Typography
        sx={{
          fontFamily: orgAiSettings.font_family || 'inherit',
          fontWeight: 600,
        }}
      >
        AI Summary
      </Typography>
    </Box>
  )

  const currentStreamStep = streamStep === 'partial_response' ? 'generation' : streamStep
  const showStreamSteps = anyLoading || liveStreamSteps.length > 0

  // Expandable step list: lives in the summary loading area, then stays above the finished answer
  const streamStepsPanel = showStreamSteps && (
    <ChatStreamSteps
      steps={liveStreamSteps}
      isStreaming={anyLoading}
      currentStep={currentStreamStep}
      orgAiSettings={orgAiSettings}
      expanded={stepsExpanded}
      onExpandedChange={setStepsExpanded}
      sx={{ px: 0, py: 0, maxWidth: '100%' }}
    />
  )

  const loadingIndicator = (
    <Box
      role='status'
      aria-live='assertive'
      tabIndex={0}
      ref={loadingRef}
      sx={{
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: 'center',
        px: combinedViewEnabled ? 0 : 3,
        py: !combinedViewEnabled ? 2 : 0,
      }}
    >
      {streamStepsPanel}
    </Box>
  )

  return (
    <Box
      ref={searchBoxRef}
      sx={{
        outline: 'none',
        ...(isMobile && {
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          height: '100vh',
        }),
      }}
      aria-label='Polimorphic Search'
    >
      {!isInline && orgAiSettings.search_show_banner_text !== false && (
        <Box
          sx={{
            backgroundColor:
              formatColor(orgAiSettings.bg_color || '') || theme.palette.aiSearch.searchBackground,
            padding: '24px',
            color: formatColor(orgAiSettings.font_color || '#000000'),
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {showLogo && (orgAiSettings.organization_ai_logo || orgAiSettings.organization_image) && (
            <Box
              role='img'
              tabIndex={0}
              aria-label={`${orgAiSettings.organization_name} logo`}
              sx={{
                display: 'inline-block',
                marginRight: 2,
                '&:focus': {
                  outline: '2px solid rgb(8, 148, 207)',
                  outlineOffset: '2px',
                  borderRadius: '50%',
                },
              }}
            >
              <Avatar
                src={
                  orgAiSettings.organization_ai_logo ??
                  orgAiSettings.organization_image ??
                  undefined
                }
                size='40px'
              />
            </Box>
          )}
          <Typography
            variant='h6'
            sx={{
              fontFamily: orgAiSettings.font_family || 'inherit',
              fontSize: isMobile ? '18px' : '22px',
            }}
          >
            {orgAiSettings.search_banner_text || (
              <>
                {resultsOnlyEnabled ? 'Search ' : 'AI-Powered Search '}for{' '}
                <strong>{orgAiSettings.organization_name}</strong>{' '}
              </>
            )}
          </Typography>
        </Box>
      )}
      <UpdatedAISearchBar
        onSearch={handleSearch}
        suggestions={suggestions}
        suggestionStyle={orgAiSettings.search_suggestion_style === 'chips' ? 'chips' : 'dropdown'}
        hasResults={submittedQuery !== '' || (response != null && response.trim() !== '')}
        query={query}
        onQueryChange={setQuery}
        onClear={handleClear}
        inputRef={searchInputRef}
        orgAiSettings={orgAiSettings}
      />
      {submittedQuery !== '' && (
        <Box sx={{ borderTop: 'none' }}>
          <Box sx={{ display: 'flex', p: '0 24px' }}>
            {!combinedViewEnabled && !resultsOnlyEnabled && (
              <Tabs
                ref={tabsRef}
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                aria-label='Search results tabs'
                slotProps={{
                  scrollButtons: {
                    tabIndex: -1, // Prevent scroll buttons from being tabbable
                  },

                  indicator: {
                    sx: {
                      bgcolor: orgAiSettings.link_color
                        ? `#${orgAiSettings.link_color}`
                        : 'rgb(8, 148, 207)',
                    },
                  },
                }}
              >
                <Tab
                  label={'AI Summary'}
                  sx={{
                    bgcolor:
                      activeTab === 0
                        ? theme.palette.aiSearch.selectedTabBackground
                        : 'transparent',
                    color: `${theme.palette.aiSearch.tabText} !important`,
                    fontFamily: orgAiSettings.font_family || 'inherit',
                    borderRadius: '6px',
                    textTransform: 'none',
                    fontWeight: 'bold',
                  }}
                  aria-label='AI generated answer'
                  role='tab'
                  tabIndex={0}
                />
                {submittedQuery !== '' && (
                  <Tab
                    label='All Results'
                    sx={{
                      bgcolor:
                        activeTab === 1
                          ? theme.palette.aiSearch.selectedTabBackground
                          : 'transparent',
                      color: `${theme.palette.aiSearch.tabText} !important`,
                      fontFamily: orgAiSettings.font_family || 'inherit',
                      borderRadius: '6px',
                      textTransform: 'none',
                      fontWeight: 'bold',
                    }}
                    aria-label='All search results'
                    role='tab'
                    tabIndex={0}
                  />
                )}
              </Tabs>
            )}
            <Box sx={{ flexGrow: 1 }} />
          </Box>
        </Box>
      )}
      {!combinedViewEnabled &&
      !resultsOnlyEnabled &&
      !(activeTab === 0 ? response : hasStreamedResults) &&
      anyLoading ? (
        activeTab === 1 ? (
          <AllResultsSkeleton count={SKELETON_RESULT_COUNT} />
        ) : (
          <>
            {loadingIndicator}
            <Box sx={{ px: 3, pb: 3 }}>
              <SummarySkeleton />
            </Box>
          </>
        )
      ) : (
        (isCombinedSummaryLoading ||
          response ||
          (resultsOnlyEnabled && submittedQuery !== '') ||
          hasStreamedResults) && (
          <>
            {showSummarySection && (
              <>
                <Box
                  role='status'
                  aria-live={streamStep === 'complete_response' ? 'polite' : 'off'}
                  aria-atomic='true'
                  sx={{ ...visuallyHidden }}
                >
                  {announcementText}
                </Box>
                <Box
                  sx={combinedViewEnabled ? combinedSummaryCardSx : { p: 3 }}
                  onLoad={postMessageScrollHeightOneLine}
                >
                  {combinedViewEnabled && combinedSummaryHeader}
                  {isCombinedSummaryLoading ? (
                    <>
                      {loadingIndicator}
                      <SummarySkeleton />
                    </>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                      }}
                    >
                      {streamStepsPanel && <Box>{streamStepsPanel}</Box>}
                      <Box
                        ref={summaryContentRef}
                        sx={
                          combinedViewEnabled
                            ? {
                                position: 'relative',
                                overflow: 'hidden',
                                ...(!summaryExpanded && {
                                  maxHeight: `${SUMMARY_COLLAPSED_HEIGHT_PX}px`,
                                }),
                              }
                            : undefined
                        }
                      >
                        {poliApplyUniqueSources.length > 0 && (
                          <Box sx={{ mt: 1 }}>
                            {poliApplyUniqueSources.map((source, index) => (
                              <StyledLink
                                key={index}
                                href={source?.page_gui_url || source?.page_url || undefined}
                                target='_blank'
                                rel='noopener noreferrer'
                                tabIndex={0}
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  textDecoration: 'none',
                                  '&:hover': { textDecoration: 'underline' },
                                  border: `1px solid #${orgAiSettings.bg_color}`,
                                  backgroundColor: `#${orgAiSettings.bg_color}22`,
                                  borderRadius: '5px',
                                  marginBottom: '20px',
                                  padding: '15px',
                                }}
                              >
                                <Box sx={{ width: '24px', marginRight: '8px' }}>
                                  <Image src={images.logo} size='24px' alt='Polimorphic logo' />
                                </Box>
                                <Typography
                                  variant='body2'
                                  sx={{
                                    fontFamily: orgAiSettings.font_family || 'inherit',
                                  }}
                                >
                                  Click to Apply Online: {source.title}
                                </Typography>
                              </StyledLink>
                            ))}
                          </Box>
                        )}
                        <Box
                          role='article'
                          aria-label='AI generated answer'
                          sx={{
                            fontFamily: orgAiSettings.font_family || 'inherit',
                            '& a': {
                              ...theme.typography.link,
                              color: orgAiSettings?.link_color
                                ? `#${orgAiSettings.link_color}`
                                : theme.palette.primary.dark,
                              textDecoration: 'underline',
                              textDecorationThickness: '1px',
                              textUnderlineOffset: '2px',
                              fontWeight: 'bold',
                              target: '_blank',
                              rel: 'noopener noreferrer',
                              fontFamily: 'inherit',
                              '&:hover': {
                                textDecorationThickness: '2px',
                              },
                              '&:focus-visible': {
                                boxShadow: `0 0 0 2px ${theme.palette.input.hover}, 0 0 0 4px ${theme.palette.input.focus}`,
                              },
                            },
                            '& p': {
                              display: 'block',
                              margin: '1.5 0',
                              lineHeight: 1.6,
                              fontFamily: 'inherit',
                            },
                            '& li': {
                              marginBottom: 1,
                              lineHeight: 1.6,
                              fontFamily: 'inherit',
                            },
                            '& li p': {
                              display: 'inline',
                              margin: 0,
                              fontFamily: 'inherit',
                            },
                            '& ul, & ol': {
                              marginTop: 1.5,
                              marginBottom: 1.5,
                              paddingLeft: 3,
                            },
                            '& h1, & h2, & h3, & h4, & h5, & h6': {
                              margin: '3 0 1.5',
                              lineHeight: 1.3,
                              fontFamily: 'inherit',
                            },
                            '& h1': { fontSize: 24 },
                            '& h2': { fontSize: 20 },
                            '& h3': { fontSize: 18 },
                            '& blockquote': {
                              borderLeft: '4px solid #e0e0e0',
                              margin: '2 0',
                              padding: '1 0 1 2',
                              fontStyle: 'italic',
                              color: 'text.secondary',
                              fontFamily: 'inherit',
                            },
                            '& code': {
                              backgroundColor: '#f5f5f5',
                              padding: '0.5 1',
                              borderRadius: '3px',
                              fontSize: 14,
                              fontFamily: 'monospace',
                            },
                            '& pre': {
                              backgroundColor: '#f5f5f5',
                              padding: 2,
                              borderRadius: '4px',
                              overflow: 'auto',
                              margin: '2 0',
                            },
                            '& pre code': {
                              padding: 0,
                              backgroundColor: 'transparent',
                            },
                          }}
                        >
                          <div
                            ref={contentRef}
                            className='prose max-w-none'
                            dangerouslySetInnerHTML={{ __html: sanitizeAndProcessLinks(response) }}
                          />
                        </Box>
                        {responseId && (
                          <Box
                            sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1 }}
                            role='group'
                            aria-labelledby={feedbackLabelId}
                          >
                            <Typography
                              id={feedbackLabelId}
                              sx={{
                                fontFamily: orgAiSettings.font_family || 'inherit',
                                color: theme.palette.text.secondary,
                              }}
                            >
                              Was this answer helpful?
                            </Typography>
                            <IconButton
                              onClick={onThumbUpClick}
                              color={impression === true ? 'success' : 'default'}
                              aria-label='Mark as helpful'
                              aria-pressed={impression === true}
                              sx={{
                                ...focusRingSx,
                                ...(impression === true
                                  ? {
                                      backgroundColor: (theme.palette.success as CustomPaletteColor)
                                        .states.selected,
                                    }
                                  : { color: theme.palette.text.secondary }),
                                ...(showInitialAnimation &&
                                  !disableAnimations && {
                                    animation: `${initialRenderAnimation} 2s ease-out`,
                                  }),
                                ...(!disableAnimations && {
                                  '&:hover': {
                                    animation: `${bounceAnimation} 0.6s ease-in-out`,
                                  },
                                }),
                              }}
                            >
                              {impression === true ? <ThumbUpAltIcon /> : <ThumbUpOffAltIcon />}
                            </IconButton>
                            <IconButton
                              onClick={onThumbDownClick}
                              color={impression === false ? 'warning' : 'default'}
                              aria-label='Mark as not helpful'
                              aria-pressed={impression === false}
                              sx={{
                                ...focusRingSx,
                                ...(impression === false
                                  ? {
                                      backgroundColor: (theme.palette.warning as CustomPaletteColor)
                                        .states.selected,
                                    }
                                  : { color: theme.palette.text.secondary }),
                                ...(showInitialAnimation &&
                                  !disableAnimations && {
                                    animation: `${initialRenderAnimation} 2s ease-out`,
                                  }),
                                ...(!disableAnimations && {
                                  '&:hover': {
                                    animation: `${bounceAnimation} 0.6s ease-in-out`,
                                  },
                                }),
                              }}
                            >
                              {impression === false ? (
                                <ThumbDownAltIcon />
                              ) : (
                                <ThumbDownOffAltIcon />
                              )}
                            </IconButton>
                            <Box
                              role='status'
                              aria-live='polite'
                              aria-atomic='true'
                              sx={visuallyHidden}
                            >
                              {feedbackAnnouncement}
                            </Box>
                            {test && !!feedback && (
                              <Typography variant='caption'>Feedback recorded</Typography>
                            )}
                            {test && (
                              <ResponseNegativeFeedbackPopover
                                submitFeedback={submitFeedback}
                                {...submitPopoverProps}
                              />
                            )}
                            {test && (
                              <ClearNegativeFeedbackPopover
                                onConfirm={clearFeedback}
                                {...clearPopoverProps}
                              />
                            )}
                          </Box>
                        )}
                        {combinedViewEnabled && !summaryExpanded && summaryOverflows && (
                          <Box
                            aria-hidden='true'
                            sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              height: '48px',
                              background: `linear-gradient(transparent, ${theme.palette.background.paper})`,
                              pointerEvents: 'none',
                            }}
                          />
                        )}
                      </Box>
                      {combinedViewEnabled && summaryOverflows && (
                        <Button
                          variant='outlined'
                          maxWidth='100%'
                          fullWidth
                          onClick={() => setSummaryExpanded(!summaryExpanded)}
                          endIcon={summaryExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                          aria-expanded={summaryExpanded}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontFamily: orgAiSettings.font_family || 'inherit',
                          }}
                        >
                          {summaryExpanded ? 'Show less' : 'Show more'}
                        </Button>
                      )}
                    </Box>
                  )}
                </Box>
                {responseSources.length > 0 && (
                  <SummarySourcesList
                    sources={responseSources}
                    maxSources={5}
                    theme={theme}
                    orgAiSettings={orgAiSettings}
                  />
                )}
              </>
            )}
            {showAllResultsSection && (
              <SearchResultsList
                sources={allResultsSources}
                pageSize={pageSize}
                totalResults={totalResults}
                totalPages={totalPages}
                page={page}
                onPageChange={setPage}
                showCount={showResultsCount && !showResultsSkeleton}
                showEmptyState={
                  showResultsCount &&
                  !anyLoading &&
                  totalResults === 0 &&
                  // Combined view already shows the error in the summary card
                  (!searchError || resultsOnlyEnabled)
                }
                emptyMessage={searchError ?? undefined}
                listRef={allResultsRef}
                isLoading={showResultsSkeleton}
                pendingCount={
                  allResultsFromStreamEnabled && isStreamingInProgress
                    ? Math.max(pageSize - streamedAllResults.length, 0)
                    : 0
                }
                debug={debug}
                theme={theme}
                isDarkMode={isDarkMode}
                orgAiSettings={orgAiSettings}
                updatedView={combinedViewEnabled}
              />
            )}
            {!isInline && orgAiSettings.search_url && !isCombinedSummaryLoading && (
              <NotFoundHelp
                websiteSearchUrl={orgAiSettings.search_url || ''}
                theme={theme}
                orgAiSettings={orgAiSettings}
              />
            )}
          </>
        )
      )}
      <PrivacyPolicyMessage
        orgAiSettings={orgAiSettings}
        fontFamily={orgAiSettings?.font_family || undefined}
        fontSize={isMobile ? '12px' : '14px'}
        historyType='search'
      />
      {!isInline && (
        <SearchFooter
          isSuper={isSuper || false}
          debugVisible={debugVisible}
          onToggleDebug={setDebugVisible}
          theme={theme}
        />
      )}
    </Box>
  )
}

interface NotFoundHelpProps {
  websiteSearchUrl: string
  theme: Theme
  orgAiSettings?: EnrichedOrganizationAISettings
}

const NotFoundHelp: React.FC<NotFoundHelpProps> = ({ websiteSearchUrl, theme, orgAiSettings }) => {
  return (
    <Box
      sx={{
        m: 3,
        borderTop: '1px solid #888',
      }}
    >
      <Typography
        variant='h3'
        sx={{
          fontFamily: orgAiSettings?.font_family || 'inherit',
          margin: '20px 0 30px',
        }}
      >
        Not finding what you are looking for?
      </Typography>
      <Typography
        sx={{
          fontFamily: orgAiSettings?.font_family || 'inherit',
          fontSize: '16px',
          letterSpacing: '0.25px',
          lineHeight: '24px',
          mt: 1.875,
        }}
      >
        Try rephrasing your question, or being more specific. If your initial question was too broad
        or vague, try narrowing it down and providing more specific details. If you need critical or
        complex information that AI can't provide, consider contacting your local government
        directly.
      </Typography>
      <Box sx={{ display: 'flex', mt: 1.875 }}>
        <Link
          href={websiteSearchUrl}
          target='_blank'
          underline='none'
          sx={{
            width: 'calc(100% - 45px)',
            '&:hover': {
              bgcolor: theme.palette.aiSearch.hover,
            },
          }}
          className='double-blue-border-on-focus'
        >
          <Typography
            sx={{
              fontFamily: orgAiSettings?.font_family || 'inherit',
              fontSize: '16px',
              letterSpacing: '0.25px',
              lineHeight: '24px',
              color: theme.palette.text.primary,
            }}
          >
            Want to try searching without AI?{' '}
            <span
              style={{
                color: theme.palette.primary.main,
                fontFamily: 'inherit',
                textDecoration: 'none',
              }}
            >
              Go to Website Search
            </span>
          </Typography>
        </Link>
      </Box>
    </Box>
  )
}

const SearchFooter: React.FC<{
  isSuper?: boolean
  debugVisible?: boolean
  onToggleDebug?: (visible: boolean) => void
  theme: typeof lightTheme
}> = ({
  isSuper = false,
  debugVisible = false,
  onToggleDebug = (): void => {},
  theme,
}): React.ReactElement => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        p: 3,
        justifyContent: 'space-between',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {isSuper && (
          <Box
            sx={{
              mr: 2,
            }}
          >
            <Switch
              size='small'
              checked={debugVisible}
              onChange={(e) => onToggleDebug(e.target.checked)}
              color='primary'
              label='Debug Info'
              containerSx={{ m: 0 }}
              sx={{
                '& .MuiSwitch-thumb': {
                  backgroundColor: theme.palette.primary.main,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  )
}

const SearchView: React.FC<SearchViewProps> = ({
  orgAiSettings,
  suggestions,
  isEmbedded,
  test,
  debug,
  initialQuery,
  isInline,
  showLogo,
  deviceId,
}) => {
  const searchParams = new URLSearchParams(window?.location?.search || '')
  const isDarkMode = searchParams.get('theme') === 'dark'
  const muiTheme = useTheme()
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'))

  // In inline mode, return only the search bar
  if (isInline) {
    return (
      <Box sx={{ width: '100%' }}>
        <SearchBox
          orgAiSettings={orgAiSettings}
          suggestions={suggestions}
          test={test}
          debug={debug}
          initialQuery={initialQuery}
          isInline={isInline}
          showLogo={showLogo}
          isMobile={isMobile}
          deviceId={deviceId}
        />
      </Box>
    )
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {!isEmbedded && (
        <>
          {test && (
            <Banner
              type='testMode'
              variant='filled'
              body={`You're previewing ${orgAiSettings.organization_name} AI search. Rate replies with 👍 / 👎 to help us improve.`}
              sx={{ borderRadius: 0 }}
            />
          )}
          {orgAiSettings.search_show_header_text !== false && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: isDarkMode
                  ? darkTheme.palette.aiSearch.background
                  : formatColor(orgAiSettings.bg_color || '#f0f0f0'),
                padding: '24px',
                color: isDarkMode
                  ? darkTheme.palette.text.primary
                  : formatColor(orgAiSettings.font_color || '#000000'),
                gap: 2,
              }}
            >
              {(orgAiSettings.organization_ai_logo || orgAiSettings.organization_image) && (
                <Box
                  role='img'
                  tabIndex={0}
                  aria-label={`${orgAiSettings.organization_name} logo`}
                  sx={{
                    display: 'inline-block',
                    '&:focus': {
                      outline: '2px solid rgb(8, 148, 207)',
                      outlineOffset: '2px',
                      borderRadius: '50%',
                    },
                  }}
                >
                  <Avatar
                    src={
                      orgAiSettings.organization_ai_logo ??
                      orgAiSettings.organization_image ??
                      undefined
                    }
                    size='32px'
                  />
                </Box>
              )}
              <Typography
                tabIndex={0}
                sx={{
                  fontFamily: orgAiSettings.font_family || 'inherit',
                  fontWeight: 'bold',
                  fontSize: isMobile ? '16px' : '22px',
                }}
              >
                {orgAiSettings.search_name || orgAiSettings.organization_name}
              </Typography>
            </Box>
          )}
          {orgAiSettings.search_show_header_image !== false && (
            <img
              src={orgAiSettings.search_banner || defaultBanner}
              alt='Organization banner'
              style={{
                width: '100%',
                height: '200px',
                objectFit: 'cover',
              }}
            />
          )}
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          width: '100%',
          flexDirection: 'column',
          gap: 2,
          ...(isDarkMode && {
            backgroundColor: darkTheme.palette.aiSearch.background,
            color: darkTheme.palette.text.primary,
          }),
        }}
      >
        <Box
          sx={{
            width: '100%',
            alignItems: 'center',
          }}
        >
          <SearchBox
            orgAiSettings={orgAiSettings}
            suggestions={suggestions}
            test={test}
            debug={debug}
            initialQuery={initialQuery}
            isInline={isInline}
            showLogo={showLogo}
            isMobile={isMobile}
            deviceId={deviceId}
          />
        </Box>
      </Box>
    </Box>
  )
}

export default SearchView
